import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { useBoardMe, useUpdateBoardMe } from "@/lib/board";
import {
  useMessagePreferences,
  useUpdateMessagePreferences,
} from "@/lib/messages";
import {
  useRoundsDigestPreferences,
  useUpdateRoundsDigestPreferences,
} from "@/lib/rounds";
import "@/styles/messages.css";

export default function BoardSettings() {
  const { city } = useCity();
  const me = useBoardMe(city.slug);
  const update = useUpdateBoardMe(city.slug);
  const messagePreferences = useMessagePreferences(city.slug);
  const updateMessagePreferences = useUpdateMessagePreferences(city.slug);
  const digestPreferences = useRoundsDigestPreferences(city.slug);
  const updateDigestPreferences = useUpdateRoundsDigestPreferences(city.slug);
  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [interests, setInterests] = useState("");
  const [error, setError] = useState<string | null>(null);

  const initialised = useRef(false);
  useEffect(() => {
    if (!me.data || initialised.current) return;
    initialised.current = true;
    setDisplayName(me.data.displayName);
    setHandle(me.data.handle);
    setInterests(me.data.specialtyInterests.join(", "));
  }, [me.data]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const specialtyInterests = interests.split(",").map((value) => value.trim()).filter(Boolean);
    if (!displayName.trim()) return setError("Add a display name.");
    if (!/^[a-z0-9-]{3,24}$/.test(handle)) {
      return setError("Use 3–24 lowercase letters, numbers or hyphens for your handle.");
    }
    if (!specialtyInterests.length) return setError("Add at least one specialty interest.");
    setError(null);
    update.mutate({ displayName: displayName.trim(), handle, specialtyInterests });
  };

  return (
    <div>
      <header className="pb-board-page-heading">
        <div><p className="pb-board-eyebrow">Member profile</p><h1>Settings</h1><p>Manage how the Board addresses you and tunes your learning feed.</p></div>
      </header>
      {me.isLoading ? <div className="pb-board-state">Loading settings…</div> : null}
      {me.isError ? <div className="pb-board-state pb-board-state--error" role="alert">Settings could not be loaded. <button type="button" onClick={() => void me.refetch()}>Retry</button></div> : null}
      {me.data ? (
        <div className="pb-board-settings-grid">
          <section className="pb-board-card pb-board-settings-card" aria-labelledby="profile-settings-heading">
            <h2 id="profile-settings-heading">Board profile</h2>
            <form onSubmit={submit}>
              <label>Display name<input required value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
              <label>Handle<span className="pb-board-input-prefix">@</span><input required minLength={3} maxLength={24} pattern="[a-z0-9-]{3,24}" value={handle} onChange={(event) => setHandle(event.target.value.toLowerCase())} /></label>
              <label>Email<input value={me.data.email} readOnly disabled /><small>Your sign-in email is managed by PulseBoard membership.</small></label>
              <label>Specialty interests<textarea rows={4} value={interests} onChange={(event) => setInterests(event.target.value)} placeholder="Cardiology, Dermatology" /><small>Separate interests with commas.</small></label>
              <button className="pb-board-primary" type="submit" disabled={update.isPending}>{update.isPending ? "Saving…" : "Save settings"}</button>
              {error ? <p className="pb-board-form-error" role="alert">{error}</p> : null}
              {update.isError ? <p className="pb-board-form-error" role="alert">Settings could not be saved. The handle may already be in use.</p> : null}
              {update.isSuccess ? <p className="pb-board-form-success" role="status">Settings saved.</p> : null}
            </form>
          </section>
          <aside className="pb-board-card pb-board-settings-card">
            <h2>Email preferences</h2>
            <p>Each PulseBoard Brief email includes your personalised preferences and unsubscribe link. For security, that page requires the token in your email.</p>
            <Link to="/brief">View Brief archive and subscription instructions</Link>
          </aside>
          <section className="pb-board-card pb-board-settings-card" aria-labelledby="rounds-preferences-heading">
            <h2 id="rounds-preferences-heading">Rounds preferences</h2>
            {digestPreferences.isLoading ? (
              <p>Loading Rounds preferences…</p>
            ) : digestPreferences.isError || !digestPreferences.data ? (
              <p role="alert">
                Rounds preferences could not be loaded. <button type="button" onClick={() => void digestPreferences.refetch()}>Retry</button>
              </p>
            ) : (
              <div className="pb-board-preferences">
                <label className="pb-board-preference-row">
                  <span>
                    <strong>Weekly Rounds digest (Mondays)</strong>
                    <small>Receive activity from your subscribed communities and replies to your threads.</small>
                  </span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={digestPreferences.data.digestEnabled}
                    disabled={updateDigestPreferences.isPending}
                    onChange={(event) => {
                      const digestEnabled = event.target.checked;
                      updateDigestPreferences.mutate(
                        { digestEnabled },
                        {
                          onSuccess: () =>
                            track("settings_digest_toggle", { enabled: digestEnabled }),
                        },
                      );
                    }}
                  />
                  <span className="pb-board-preference-switch" aria-hidden="true" />
                </label>
                {updateDigestPreferences.isError ? (
                  <p role="alert">Rounds digest preference could not be saved. Try again.</p>
                ) : null}
              </div>
            )}
          </section>
          <section className="pb-board-card pb-board-settings-card" aria-labelledby="message-preferences-heading">
            <h2 id="message-preferences-heading">Message preferences</h2>
            {messagePreferences.isLoading ? (
              <p>Loading message preferences…</p>
            ) : messagePreferences.isError || !messagePreferences.data ? (
              <p role="alert">
                Message preferences could not be loaded. <button type="button" onClick={() => void messagePreferences.refetch()}>Retry</button>
              </p>
            ) : (
              <div className="pb-message-preferences">
                <label className="pb-message-preference-row">
                  <span>
                    <strong>Colleagues can message me</strong>
                    <small>Allow new direct conversations from message-enabled members.</small>
                  </span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={messagePreferences.data.messagesEnabled}
                    disabled={updateMessagePreferences.isPending}
                    onChange={(event) =>
                      updateMessagePreferences.mutate({
                        ...messagePreferences.data,
                        messagesEnabled: event.target.checked,
                      })
                    }
                  />
                  <span className="pb-message-preference-switch" aria-hidden="true" />
                </label>
                <label className="pb-message-preference-row">
                  <span>
                    <strong>Email me about new messages</strong>
                    <small>Email notifications never include the message body.</small>
                  </span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={messagePreferences.data.emailNotify}
                    disabled={updateMessagePreferences.isPending}
                    onChange={(event) =>
                      updateMessagePreferences.mutate({
                        ...messagePreferences.data,
                        emailNotify: event.target.checked,
                      })
                    }
                  />
                  <span className="pb-message-preference-switch" aria-hidden="true" />
                </label>
                {updateMessagePreferences.isError ? (
                  <p role="alert">Message preferences could not be saved. Try again.</p>
                ) : null}
              </div>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
